// src/components/forms/fields/ProviderSelectField.tsx

import React, { useState, useEffect } from 'react';
import { User, Calendar, MapPin, Loader2 } from 'lucide-react';
import { apiService } from '../../../services/api';

interface Provider {
  id: string;
  first_name: string;
  last_name: string;
  credentials?: string;
  role: string;
  specialty?: string;
  next_available?: string;
  location_names?: string[];
}

interface ProviderSelectFieldProps {
  organizationId: string;
  locationId?: string | null;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  required?: boolean;
  disabled?: boolean;
  showSpecialty?: boolean;
  showAvailability?: boolean;
  filterByLocation?: boolean;
  allowMultiple?: boolean;
  placeholder?: string;
}

const ProviderSelectField: React.FC<ProviderSelectFieldProps> = ({
  organizationId,
  locationId,
  value,
  onChange,
  required = false,
  disabled = false,
  showSpecialty = true,
  showAvailability = false,
  filterByLocation = false,
  allowMultiple = false,
  placeholder = 'Select a provider'
}) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Record<string, string>>({});

  useEffect(() => {
    loadProviders();
  }, [organizationId, locationId, filterByLocation]);

  useEffect(() => {
    if (showAvailability && providers.length > 0) {
      loadAvailability();
    }
  }, [providers, showAvailability]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all users in the organization with provider roles
      const users = await apiService.getOrganizationUsersPublic(organizationId);
      
      let filteredProviders = users.filter(user => 
        ['Provider', 'Admin', 'Prescriber', 'User'].includes(user.role) && user.is_provider
      );


      // Filter by location if needed
      if (filterByLocation && locationId) {
        // This would need an endpoint to get provider-location assignments
        // For now, we'll show all providers with a note about location
        filteredProviders = filteredProviders.map(provider => ({
          ...provider,
          location_names: ['Main Office'] // Placeholder
        }));
      }

      setProviders(filteredProviders);
    } catch (err) {
      console.error('Error loading providers:', err);
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async () => {
    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 7); // Check next 7 days

      const availabilityPromises = providers.map(async (provider) => {
        try {
          const slots = await apiService.getAvailableSlots({
            provider_id: provider.id,
            start_date: today.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
            appointment_duration_minutes: 30
          });

          if (slots && slots.length > 0) {
            const nextSlot = slots[0];
            return {
              providerId: provider.id,
              nextAvailable: `${nextSlot.date} at ${nextSlot.start_time}`
            };
          }
          return { providerId: provider.id, nextAvailable: 'No availability' };
        } catch (err) {
          return { providerId: provider.id, nextAvailable: 'Check availability' };
        }
      });

      const results = await Promise.all(availabilityPromises);
      const availabilityMap: Record<string, string> = {};
      results.forEach(result => {
        availabilityMap[result.providerId] = result.nextAvailable;
      });
      setAvailability(availabilityMap);
    } catch (err) {
      console.error('Error loading availability:', err);
    }
  };

  const handleSingleSelect = (providerId: string) => {
    onChange(providerId);
  };

  const handleMultipleSelect = (providerId: string) => {
    const currentValue = Array.isArray(value) ? value : value ? [value] : [];
    const newValue = currentValue.includes(providerId)
      ? currentValue.filter(id => id !== providerId)
      : [...currentValue, providerId];
    onChange(newValue);
  };

  const isSelected = (providerId: string) => {
    if (allowMultiple) {
      return Array.isArray(value) ? value.includes(providerId) : value === providerId;
    }
    return value === providerId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 border rounded-lg bg-gray-50">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-500">Loading providers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <p className="text-gray-500 text-sm">No providers available</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Single select dropdown */}
      {!allowMultiple && (
        <select
          value={value as string}
          onChange={(e) => handleSingleSelect(e.target.value)}
          required={required}
          disabled={disabled}
          className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#0facd7] focus:border-[#0facd7] disabled:bg-gray-100"
        >
          <option value="">{placeholder}</option>
          {providers.map(provider => (
            <option key={provider.id} value={provider.id}>
              {provider.first_name} {provider.last_name}{provider.credentials ? `, ${provider.credentials}` : ''}
              {showSpecialty && provider.specialty && ` - ${provider.specialty}`}
            </option>
          ))}
        </select>
      )}

      {/* Multiple select checkboxes */}
      {allowMultiple && (
        <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
          {providers.map(provider => (
            <label
              key={provider.id}
              className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                isSelected(provider.id)
                  ? 'bg-blue-50 border-[#0facd7]'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected(provider.id)}
                onChange={() => handleMultipleSelect(provider.id)}
                disabled={disabled}
                className="mt-1 mr-3 text-[#0facd7] focus:ring-[#0facd7]"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">
                    {provider.first_name} {provider.last_name}{provider.credentials ? `, ${provider.credentials}` : ''}
                  </span>
                </div>
                
                {showSpecialty && provider.specialty && (
                  <p className="text-sm text-gray-600 mt-1">{provider.specialty}</p>
                )}
                
                {filterByLocation && provider.location_names && (
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {provider.location_names.join(', ')}
                    </span>
                  </div>
                )}
                
                {showAvailability && availability[provider.id] && (
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      Next: {availability[provider.id]}
                    </span>
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
      )}

      {/* Show next available for single select */}
      {!allowMultiple && showAvailability && value && availability[value as string] && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-700">
            Next available: {availability[value as string]}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProviderSelectField;